import { Div, Button } from '@codesuma/baseline'
import calendar, { IDayCell } from '../../../services/calendar'
import settings from '../../../services/settings'
import { J_MONTHS, G_MONTHS, H_MONTHS } from '../../../configs/names'
import { monthColor } from '../../../configs/colors'
import { toFaDigits } from '../../../helpers/digits'
import { solarYear, paleYear } from '../../../helpers/year'
import { dayLineParts } from '../../../helpers/day-line'
import { Happenings } from './happenings'
import { Tasks } from './tasks'
import { Notes } from './notes'
import { DayColor } from './day-color'
import { adoptRoute, eventRoute } from './phone-events'
import { AdoptDialog } from './adopt-dialog'
import { DayTab } from '../tab-bar'
import { BackIcon } from '../../../components/back-icon'
import pageHead from '../../../components/page-head/index.module.css'
import adoptionScan from '../../../services/event-adoption-scan'
import phoneCalendar from '../../../services/phone-calendar'
import phoneReminders from '../../../services/phone-reminders'
import { IPhoneEvent, fromPhone } from '../../../services/phone-events'
import style from './index.module.css'

// A Gregorian date — the day slider's anchor (slider index 0).
export interface IDayAnchor {
    gy: number
    gm: number
    gd: number
}

// One day inside the day slider: the full date as a title with the same day in
// the other two calendars under it, then one of three tabs — the day's to-dos
// with what is on it under them, what was written about it, or its colour.
// Each list ends in the line that adds one more of its own kind. Which tab is
// `tab`, chosen on the bar the page keeps under the slides; the lists carry no
// heading of their own, since the bar has already named them. The colour's
// stop on the bar is a wheel with no words, so that tab names itself.
//
// Adding leaves the day. A task and an occasion each open a wizard of their own,
// and an event opens the event editor. Each emits the day it was pressed on, not
// the anchor: the user may have swiped several days along before reaching for
// the line.
export const DaySlide = (anchor: IDayAnchor, index: number, tab: DayTab) => {
    const base = Div()
    base.addClass(style.base)
    // The tasks and events tab scrolls its lists in a band beside its margin
    // rule rather than scrolling the whole slide past it — see .baseRuled. It
    // is the only tab with marks to stand in that column, so it is the only
    // one that does.
    if (tab === 'agenda') base.addClass(style.baseRuled)

    const { system, theme, showHijri } = settings.get()
    if (system === 'gregorian') base.attr('dir', 'ltr')
    const day = calendar.dayAt(anchor.gy, anchor.gm, anchor.gd, index)

    // The slider can be swiped — or deep-linked — into a month the grid never
    // built, so this asks for that month's events and reminders too. Already
    // held once the day was reached from the calendar, in which case it costs
    // nothing.
    phoneCalendar.ensure([day])
    phoneReminders.ensure([day])

    // In the seasons theme the paper is the month's own color, so each slide
    // carries its own: swiping across a month boundary brings the new color in
    // with the slide.
    //
    // --paper rather than --paper-rgb: the shell resolves --paper against its
    // own channels once and hands the answer down, so overriding the channels
    // this far below would never reach it.
    if (theme === 'seasons') {
        base.el.style.setProperty('--paper', `rgb(${monthColor(system === 'gregorian' ? day.gm : day.jm, system)})`)
    }

    // The task page's head, on the slide: the day written out in full on one
    // bold line beside the back chevron — the page's own, floating in the
    // corner over every slide where the task page keeps its — and the same day
    // in the other calendars under it.
    const head = Div()
    head.addClass(style.head)
    base.append(head)

    // The space the page's own chevron floats in: the shared head's chevron
    // over again, invisible and out of the tab order, so the row measures the
    // same here as it does on every page that carries a real one.
    const chevronSpace = Button()
    chevronSpace.addClass(pageHead.dismiss, style.headChevron)
    chevronSpace.attr('aria-hidden', 'true')
    chevronSpace.attr('tabindex', '-1')
    chevronSpace.append(BackIcon())
    head.append(chevronSpace)

    const heading = Div()
    heading.addClass(style.heading)
    head.append(heading)

    const title = Div()
    title.addClass(pageHead.title)
    // The month and year dimmed, so the weekday and the day lead.
    const [dayPart, monthPart] = dayLineParts(day, system)
    title.html(`${dayPart} <span style="opacity:.35">${monthPart}</span>`)
    heading.append(title)

    // The same day in the calendars the title is not in: the other solar one
    // first, then the hijri — the order and the spelling the month header uses
    // (Persian month name with Latin digits for the Gregorian date), and the
    // hijri only when the reader asked for hijri days, exactly as the month
    // header does it.
    const gregorian = `${day.gd} ${G_MONTHS[day.gm - 1]} ${day.gy}`
    const jalaali = `${toFaDigits(day.jd)} ${J_MONTHS[day.jm - 1]} ${solarYear(day.jy)}`
    const hijri = `${toFaDigits(day.hd)} ${H_MONTHS[day.hm - 1]} ${toFaDigits(day.hy)}`
    const other = system === 'gregorian' ? jalaali : gregorian
    const sub = Div()
    sub.html(paleYear(showHijri ? `${other} | ${hijri}` : other))
    sub.addClass(pageHead.date, style.sub)
    // Persian throughout, so RTL even on the LTR Gregorian slide.
    if (system === 'gregorian') sub.attr('dir', 'rtl')
    heading.append(sub)

    // Set by the notes tab, and by nothing else: it is the one list with a
    // field in it. See focusNotes on the returned slide.
    let focusNotes: (() => void) | undefined

    // Every list is Persian prose — RTL even on the LTR Gregorian slide.
    const body = Div()
    body.addClass(style.body)
    if (system === 'gregorian') body.attr('dir', 'rtl')
    base.append(body)

    switch (tab) {
        // The tasks first, as the tab names them, and what is on the day under
        // them.
        case 'agenda': {
            const tasks = Tasks(day)
            tasks.on('add-task', (c: IDayCell) => base.emit('add-task', c))
            // A task put off or deleted: the day it left and the day it joined
            // both have to be drawn again, and the one it joined may be a slide
            // already built beside this one.
            tasks.on('changed', () => base.emit('tasks-changed'))
            body.append(tasks)

            const happenings = Happenings(day)
            happenings.on('add', (c: IDayCell) => base.emit('add-happening', c))
            const phoneEvents = happenings.events
            // Emrouz's own events open its editor. The phone's open the phone's
            // own calendar on this day, where they are changed: Emrouz only
            // reads them. Noon rather than the event's start, which for an
            // all-day event is a midnight that can fall on the day before.
            phoneEvents.on('open', (event: IPhoneEvent) => {
                if (fromPhone(event)) phoneCalendar.openPhone(new Date(day.gy, day.gm - 1, day.gd, 12).getTime())
                else base.emit('open-event', eventRoute(day, event))
            })
            // The mark on a yearly event. Asking is the whole of it — the
            // answer either opens the occasion wizard or writes the event off
            // for good.
            phoneEvents.on('adopt', (event: IPhoneEvent) => {
                const dialog = AdoptDialog(event.title)
                const close = () => dialog.remove()
                dialog.on('convert', () => {
                    close()
                    base.emit('open-event', adoptRoute(day, event))
                })
                dialog.on('keep', () => {
                    close()
                    adoptionScan.keep(event.id)
                })
                dialog.on('close', close)
                base.append(dialog)
            })
            body.append(happenings)

            // The margin rule both lists stand against. The slide draws it, so
            // it goes with its day when the days are swiped (see .rule).
            const rule = Div()
            rule.addClass(style.rule)
            base.append(rule)
            break
        }
        case 'notes': {
            const dayNotes = Notes(day)
            dayNotes.on('open-note', (id: string) => base.emit('open-note', id))
            body.append(dayNotes)
            // A note is written in the tab, so the tab has a caret to put
            // somewhere. Only the page knows when it has just been picked.
            focusNotes = dayNotes.focusField
            break
        }
        case 'color': {
            body.append(DayColor(day))
            break
        }
    }

    return Object.assign(base, {
        // Puts the caret in the notes field, when this slide is showing that
        // tab and it has a field open. A no-op otherwise.
        focusNotes: () => focusNotes?.(),
    })
}
